import type { PreviewResult } from "../../lib/api";
import { SeverityBadge, Th, Td } from "./ui";

export function SummaryTab({ data }: { data: PreviewResult }) {
  const { summary, warnings, durationMs } = data;

  const statCards = [
    { label: "Devices / Assets", value: summary.datasetCount },
    { label: "Containers", value: summary.containerCount },
    { label: "Tags", value: summary.fieldCount },
    { label: "Pipelines", value: summary.dataFlowCount },
    { label: "Jobs", value: summary.dataJobCount },
  ].filter((s) => s.value > 0);

  const durationLabel = durationMs < 1000 ? `${durationMs} ms` : `${(durationMs / 1000).toFixed(1)} s`;

  return (
    <div className="space-y-5">
      {statCards.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {statCards.map((s) => (
            <div key={s.label} className="card px-4 py-5 text-center">
              <div className="font-mono text-[32px] font-semibold tabular-nums text-ink">{s.value.toLocaleString()}</div>
              <div className="mt-1 text-[11.5px] text-ash">{s.label}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card px-6 py-10 text-center">
          <p className="text-[13.5px] text-ash">No entities were emitted by this source.</p>
        </div>
      )}

      <div className="flex items-center justify-between text-[12px]">
        <span className="text-ash">Parsed in {durationLabel}</span>
        {warnings.length === 0 ? (
          <span className="flex items-center gap-1 font-medium text-emerald-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" clipRule="evenodd" />
            </svg>
            All checks passed
          </span>
        ) : (
          <span className="font-medium text-amber-600">{warnings.length} issue{warnings.length !== 1 ? "s" : ""} detected</span>
        )}
      </div>

      {warnings.length > 0 && (
        <section className="card overflow-hidden">
          <div className="border-b border-rule px-6 py-4">
            <h2 className="font-display text-[16px] font-semibold tracking-tight text-ink">
              Issues detected <span className="ml-1 font-mono text-[12px] text-ash">{warnings.length}</span>
            </h2>
          </div>
          <table className="w-full text-[13px]">
            <thead className="border-b border-rule bg-paper text-left">
              <tr>
                <Th className="w-24">Severity</Th>
                <Th>Message</Th>
                <Th>Entity</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {warnings.map((w, i) => (
                <tr key={i} className="hover:bg-paper">
                  <Td><SeverityBadge severity={w.severity} /></Td>
                  <Td><span className="text-ink">{w.message}</span></Td>
                  <Td>{w.entity ? <span className="font-mono text-[11px] text-ash break-all">{w.entity}</span> : <span className="text-ash">—</span>}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
